import React, { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
type AnchoredPopoverProps = {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  width: number;
  placement?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  offset?: number;
  children: React.ReactNode;
};

/** Floating layer rendered into <body> so scrolling panels and modals never clip it. */
export function AnchoredPopover({
  anchorRef,
  open,
  onClose,
  width,
  placement = 'bottom',
  align = 'start',
  offset = 6,
  children
}: AnchoredPopoverProps) {
  const popRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => anchorRef.current && setRect(anchorRef.current.getBoundingClientRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchorRef]);
  let style: React.CSSProperties = {};
  if (rect) {
    const rawLeft = align === 'start' ? rect.left : align === 'end' ? rect.right - width : rect.left + rect.width / 2 - width / 2;
    const left = Math.max(8, Math.min(window.innerWidth - width - 8, rawLeft));
    style = placement === 'bottom' ? {
      left,
      top: rect.bottom + offset,
      width
    } : {
      left,
      bottom: window.innerHeight - rect.top + offset,
      width
    };
  }
  return createPortal(<AnimatePresence data-id="element-810" data-mp="2ok9k29eom5cn">
      {open && rect && <motion.div ref={popRef} style={{
      position: 'fixed',
      ...style,
      transformOrigin: `${placement === 'bottom' ? 'top' : 'bottom'} ${align === 'end' ? 'right' : align === 'start' ? 'left' : 'center'}`
    }} className="z-[80]" initial={{
      opacity: 0,
      scale: 0.96,
      y: placement === 'bottom' ? -4 : 4
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96
    }} transition={{
      duration: 0.15,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-811" data-mp="2jo2j6v157k1k">
        
          {children}
        </motion.div>}
    </AnimatePresence>, document.body);
}